import {
  FieldFilter,
  IsNullFilter,
  EqualFilter,
  NotEqualFilter,
  InFilter,
  NotInFilter,
  LikeFilter,
  GreaterThanOrEqualFilter,
  LessThanOrEqualFilter,
  NumericOperator,
  BetweenFilter,
} from "./FieldFilter.model";

export type FilterMenuTypeKind =
  | "single"
  | "multi"
  | "search"
  | "boolean"
  | "numeric"
  | "dateRange";

export const numericOperatorToLabel: Record<NumericOperator, string> = {
  gte: "at least",
  lte: "at most",
};

export type DateOperator = "between" | "gte" | "lte";
export const dateOperatorToLabel: Record<DateOperator, string> = {
  between: "is between",
  gte: "is after",
  lte: "is before",
};

export interface FilterMenuItemOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

export function isFilterMenuItemOption(
  option: FilterMenuOption
): option is FilterMenuItemOption {
  return "value" in option && "label" in option;
}

export interface FilterMenuItemOptionGroup {
  label: string;
  options: FilterMenuItemOption[];
}

export function isFilterMenuItemOptionGroup(
  option: FilterMenuOption
): option is FilterMenuItemOptionGroup {
  return "options" in option && "label" in option;
}

export type FilterMenuOption = FilterMenuItemOption | FilterMenuItemOptionGroup;

interface BaseFilterMenuItemType<
  Field extends string,
  Value extends FieldFilter
> {
  kind: FilterMenuTypeKind;
  field: Field;
  value?: Value;
  label: string;
  icon?: React.ReactNode;
}

export interface SingleFilterMenuItemType<Field extends string>
  extends BaseFilterMenuItemType<
    Field,
    EqualFilter | NotEqualFilter | IsNullFilter
  > {
  kind: "single";
  options: FilterMenuItemOption[];
  disableOperator?: boolean;
}

export interface MultiFilterMenuItemType<Field extends string>
  extends BaseFilterMenuItemType<Field, InFilter | NotInFilter | IsNullFilter> {
  kind: "multi";
  options: FilterMenuOption[];
  searchable?: boolean;
  keepOpenOnSelect?: boolean;
  disableOperator?: boolean;
}

export interface SearchFilterMenuItemType<Field extends string>
  extends BaseFilterMenuItemType<Field, LikeFilter> {
  kind: "search";
  placeholder?: string;
  defaultValue?: string;
}

export interface BooleanFilterMenuItemType<Field extends string>
  extends BaseFilterMenuItemType<
    Field,
    EqualFilter | NotEqualFilter | IsNullFilter
  > {
  kind: "boolean";
  trueLabel?: string;
  falseLabel?: string;
}

export interface NumericFilterMenuItemType<Field extends string>
  extends BaseFilterMenuItemType<
    Field,
    GreaterThanOrEqualFilter | LessThanOrEqualFilter
  > {
  kind: "numeric";
}

/** Epoch-millisecond field, picked as a calendar date or range. */
export type DateFilterValue =
  | BetweenFilter
  | GreaterThanOrEqualFilter
  | LessThanOrEqualFilter;

export interface DateRangeFilterMenuItemType<Field extends string>
  extends BaseFilterMenuItemType<Field, DateFilterValue> {
  kind: "dateRange";
}

export type FilterMenuItemType<Field extends string> =
  | SingleFilterMenuItemType<Field>
  | MultiFilterMenuItemType<Field>
  | SearchFilterMenuItemType<Field>
  | BooleanFilterMenuItemType<Field>
  | NumericFilterMenuItemType<Field>
  | DateRangeFilterMenuItemType<Field>;

export type FilterMenuType<Field extends PropertyKey> = FilterMenuItemType<
  Extract<Field, string>
>[];
